import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-6 shadow-lg">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8" />
              World Trademark Register
            </h1>
            <Link to="/">
              <Button variant="outline" className="bg-white text-blue-900 hover:bg-gray-100">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto max-w-5xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-4xl font-bold text-blue-900 text-center mb-4">
              Privacy Policy
            </CardTitle>
            <p className="text-center text-gray-600">
              Your privacy is important to us
            </p>
          </CardHeader>
          <CardContent className="prose max-w-none p-8 md:p-12">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                World Trademark Register ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our trademark registration services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Information We Collect</h2>
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Register for our services or create an account</li>
                <li>Fill out contact forms or request information</li>
                <li>Subscribe to our newsletter or communications</li>
                <li>Submit trademark applications through our platform</li>
                <li>Communicate with us via email, phone, or chat</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                This personal information may include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Full name and contact information (email address, phone number, mailing address)</li>
                <li>Business information (company name, business address, industry)</li>
                <li>Trademark information (mark details, goods/services descriptions, specimens)</li>
                <li>Payment information (credit card details, billing address)</li>
                <li>Professional information (title, role in organization)</li>
              </ul>

              <h3 className="text-xl font-semibold text-blue-900 mb-3 mt-6">Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you visit our website, we automatically collect certain information about your device and usage patterns, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>IP address, browser type, and operating system</li>
                <li>Pages viewed, links clicked, and time spent on pages</li>
                <li>Referring website addresses and search terms used</li>
                <li>Device identifiers and mobile network information</li>
                <li>Cookies and similar tracking technologies data</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for various legitimate business purposes, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Service Delivery:</strong> To process and manage your trademark registration applications, provide customer support, and deliver the services you request</li>
                <li><strong>Communication:</strong> To send you service-related notices, updates about your application status, and respond to your inquiries</li>
                <li><strong>Marketing:</strong> To send you promotional materials, newsletters, and information about our services (you can opt out at any time)</li>
                <li><strong>Improvement:</strong> To analyze usage patterns, improve our website functionality, and enhance user experience</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations, enforce our terms and conditions, and protect our rights and property</li>
                <li><strong>Security:</strong> To detect, prevent, and address fraud, security issues, and technical problems</li>
                <li><strong>Record Keeping:</strong> To maintain accurate records of our services and your transactions with us</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Government Agencies:</strong> We share trademark application information with the USPTO and other relevant government authorities as required to process your applications</li>
                <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website, conducting business, or servicing you (e.g., payment processors, email service providers)</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information when required by law, court order, or government regulation, or to protect our rights, property, or safety</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity</li>
                <li><strong>With Your Consent:</strong> We may share your information for any other purpose with your explicit consent</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                All third-party service providers are contractually required to maintain the confidentiality and security of your information and are prohibited from using it for any purpose other than providing services to us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Encryption of data in transit using SSL/TLS protocols</li>
                <li>Secure storage of sensitive information with encryption at rest</li>
                <li>Regular security assessments and vulnerability testing</li>
                <li>Access controls and authentication requirements for our systems</li>
                <li>Employee training on data protection and confidentiality</li>
                <li>Regular backups and disaster recovery procedures</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Access:</strong> You have the right to request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> You can request that we correct inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> You may request deletion of your personal information, subject to legal retention requirements</li>
                <li><strong>Opt-Out:</strong> You can opt out of receiving marketing communications from us at any time</li>
                <li><strong>Data Portability:</strong> You may request a copy of your data in a structured, machine-readable format</li>
                <li><strong>Restriction:</strong> You can request that we restrict processing of your information in certain circumstances</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                To exercise these rights, please contact us using the information provided at the end of this policy. We will respond to your request within a reasonable timeframe as required by applicable law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Provide personalized content and advertisements</li>
                <li>Improve website functionality and performance</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                You can control cookie preferences through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites, services, or resources. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected information from a child, we will take steps to delete such information promptly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country. We take appropriate measures to ensure that your information receives an adequate level of protection wherever it is processed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on our website with a new effective date. Your continued use of our services after any changes indicates your acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>World Trademark Register</strong></p>
                <p className="text-gray-700 mb-2">40 Wall St 33rd Floor, Suite 3302</p>
                <p className="text-gray-700 mb-2">New York, NY 10005</p>
                <p className="text-gray-700 mb-2">Phone: +1 (123) 123-4567</p>
                <p className="text-gray-700">Email: info@worldtrademarkregister.com</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
